import { logger } from '../utils/logger';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  CloudUpload,
  Business,
  Store,
  Analytics,
  CheckCircle,
  ArrowForward,
  ArrowBack,
  Help,
  Upload,
  Download,
  FileUpload,
  Person,
  Close,
  Warning
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import ProductTaxCodeDialog from './tax/ProductTaxCodeDialog';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import { validateEmail, validatePassword } from '../utils/validation';
import { Login } from './auth/Login';
import { Register } from './auth/Register';
import { PLATFORM_OPTIONS } from '../utils/platforms';
import {
  handleQuickBooksImport as sharedHandleQuickBooksImport,
} from '../utils/quickbooksImport';
import { handleShopifyImport as sharedHandleShopifyImport } from '../utils/shopifyImport';
import { API_URL, api } from '../services/api';
import { companyService } from '../services/companyService';
import GoogleSignIn from './GoogleSignIn';
import { AuthenticationError } from '../services/companyService';
import { GooglePlacesAutocompleteProxy } from './common/GooglePlacesAutocompleteProxy';
import CompanySelector from './CompanySelector';
import { Company } from '../types/company';
import { ImportDateRangeControls } from './ImportDateRangeControls';
import { useSharedImportDateRange } from '../hooks/useSharedImportDateRange';
import { sanitizeImportDateRange } from '../utils/importDateRange';
import { filterTaxIdWarning } from '../utils/taxAccountWarningUtils';

import { isValidEin, EIN_ERROR } from '../utils/ein';
interface OnboardingWizardProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface CompanyFormState {
  name: string;
  tax_id: string;
  email: string;
  phone: string;
  website: string;
  address_line1: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  tax_code: string;
  is_head_office: boolean;
}

const emptyCompanyForm: CompanyFormState = {
  name: '',
  tax_id: '',
  email: '',
  phone: '',
  website: '',
  address_line1: '',
  city: '',
  state: '',
  zip_code: '',
  country: 'US',
  tax_code: '',
  is_head_office: true,
};

const TAX_ACCOUNT_WARNING_DEFAULT =
  'No connected account configured for accurate tax calculation. Save the company again to retry the account connection.';

type SaveCompanyResult = 'created' | 'updated' | null;
type CompanyApiEntity = Company | null;

const isCompanyEntity = (entity: CompanyApiEntity): entity is Company => {
  return !!entity;
};

const extractCompanyId = (entity: CompanyApiEntity): number | null => {
  return entity?.id ?? null;
};

const isPlaceholderCompanyName = (companyName: string, userFullName?: string): boolean => {
  if (!companyName) {
    return false;
  }
  const normalized = companyName.trim().toLowerCase();
  if (normalized === "default company") {
    return true;
  }
  if (userFullName && userFullName.trim()) {
    const base = `${userFullName.trim()}'s company`.toLowerCase();
    if (normalized.startsWith(base)) {
      const remainder = normalized.slice(base.length).trim();
      if (!remainder || remainder.startsWith("(")) {
        return true;
      }
    }
  }
  return false;
};

const steps = [
  {
    label: 'Welcome to GoTruTax',
    description: 'Let\'s get you started with sales tax compliance',
    icon: <CheckCircle />,
  },
  {
    label: 'Sign In or Register',
    description: 'Create your account to get started',
    icon: <Person />,
  },
  {
    label: 'Update Your Company',
    description: 'Update your company details for tax compliance',
    icon: <Business />,
  },
  {
    label: 'Import Your Transactions',
    description: 'Choose how you want to import your sales data',
    icon: <Upload />,
  },
  {
    label: 'Review Tax Insights',
    description: 'See your tax analysis and compliance status',
    icon: <Analytics />,
  },
  {
    label: 'You\'re All Set!',
    description: 'Your tax compliance journey begins now',
    icon: <CheckCircle />,
  },
];

const SAMPLE_CSV_URL = '/sample-transactions.csv';

const importOptions = [
  {
    title: 'Upload CSV File',
    description: 'Upload your sales transactions from a CSV file',
    icon: <CloudUpload sx={{ fontSize: 40 }} />, 
    action: 'upload',
    features: ['Drag & drop support', 'Automatic column mapping', 'Bulk import'],
  },
  {
    title: 'Connect QuickBooks',
    description: 'Sync your transactions directly from QuickBooks',
    icon: <Business sx={{ fontSize: 40 }} />, 
    action: 'quickbooks',
    features: ['Real-time sync', 'Automatic updates', 'Secure connection'],
  },
  {
    title: 'Connect Shopify',
    description: 'Import your Shopify store transactions',
    icon: <Store sx={{ fontSize: 40 }} />, 
    action: 'shopify',
    features: ['Live data sync', 'Order tracking', 'Tax calculation'],
  },
];

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  open = false,
  onClose,
  onComplete,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedImportMethod, setSelectedImportMethod] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [importCompanies, setImportCompanies] = useState<Company[]>([]);
  const [importCompaniesLoading, setImportCompaniesLoading] = useState<boolean>(false);
  const [importCompanyError, setImportCompanyError] = useState<string | null>(null);
  const [importCompanyId, setImportCompanyId] = useState<number>(0);
  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', confirmPassword: '', full_name: '' });
  const [loginError, setLoginError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerValidation, setRegisterValidation] = useState<{ email?: string; password?: string; confirmPassword?: string; full_name?: string }>({});
  const [shopifyStore, setShopifyStore] = useState('');
  const {
    range: importDateRange,
    setRange: setImportDateRange,
    error: importDateRangeError
  } = useSharedImportDateRange();
  const [companyForm, setCompanyForm] = useState<CompanyFormState>(emptyCompanyForm);
  const [companyFormInitial, setCompanyFormInitial] = useState<CompanyFormState>(emptyCompanyForm);
  const [companySuccessMessage, setCompanySuccessMessage] = useState<string | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [companyWarning, setCompanyWarning] = useState<string | null>(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);
  const [createdCompanyId, setCreatedCompanyId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { user, login, register, createCompany, refreshUser, logout, isAuthenticated } = useAuth();

  const loadImportCompanies = useCallback(async () => {
    if (!user) {
      setImportCompanies([]);
      setImportCompanyId(0);
      setImportCompaniesLoading(false);
      setImportCompanyError(null);
      return;
    }

    setImportCompaniesLoading(true);
    setImportCompanyError(null);
    try {
      const fetched = await companyService.listCompanies();
      setImportCompanies(fetched);
      if (!fetched.length) {
        setImportCompanyId(0);
        return;
      }

      setImportCompanyId((prev) => {
        if (prev && fetched.some((c) => c.id === prev)) {
          return prev;
        }
        if (createdCompanyId && fetched.some((c) => c.id === createdCompanyId)) {
          return createdCompanyId;
        }
        if (user.primary_company_id && fetched.some((c) => c.id === user.primary_company_id)) {
          return user.primary_company_id;
        }
        return fetched[0].id;
      });
    } catch (err) {
      logger.error('[OnboardingWizard] Failed to load companies for import:', err);
      setImportCompanies([]);
      setImportCompanyError('Failed to load companies. Please try again.');
    } finally {
      setImportCompaniesLoading(false);
    }
  }, [user, createdCompanyId]);

  useEffect(() => {
    if (!open || !user) {
      return;
    }
    loadImportCompanies();
  }, [open, user, loadImportCompanies]);

  useEffect(() => {
    if (createdCompanyId) {
      loadImportCompanies();
    }
  }, [createdCompanyId, loadImportCompanies]);

  // Track if company exists (404 means it doesn't)
  const [companyExists, setCompanyExists] = useState<boolean>(true);
  
  // Track if user has imported transactions
  const [hasTransactions, setHasTransactions] = useState<boolean>(false);
  const [transactionCount, setTransactionCount] = useState<number>(0);

  const hasCompanyChanges = useMemo(() => {
    const keys: (keyof CompanyFormState)[] = [
      'name',
      'tax_id',
      'email',
      'phone',
      'website',
      'address_line1',
      'city',
      'state',
      'zip_code',
      'country',
      'tax_code',
      'is_head_office'
    ];

    return keys.some((key) => companyForm[key] !== companyFormInitial[key]);
  }, [companyForm, companyFormInitial]);

  useEffect(() => {
    if (companySuccessMessage && hasCompanyChanges) {
      setCompanySuccessMessage(null);
    }
  }, [companySuccessMessage, hasCompanyChanges]);

  // Load company data when user is authenticated and has a company
  useEffect(() => {
    const loadCompanyData = async () => {
      // Skip if no user or no company_id
      if (!user || !user.primary_company_id) {
        logger.log('[OnboardingWizard] No user or company_id, skipping company data load');
        setCompanyExists(false);
        setCompanyForm(emptyCompanyForm);
        setCompanyFormInitial(emptyCompanyForm);
        setCompanySuccessMessage(null);
        setCompanyWarning(null);
        return;
      }
      
      try {
        const company = await companyService.getCompany(user.primary_company_id);
        if (company) {
          const formData: CompanyFormState = {
            name: company.name || '',
            tax_id: company.tax_id || '',
            email: company.email || '',
            phone: company.phone || '',
            website: company.website || '',
            address_line1: company.address_line1 || '',
            city: company.city || '',
            state: company.state || '',
            zip_code: company.zip_code || '',
            country: company.country || 'US',
            tax_code: company.tax_code || '',
            is_head_office: company.is_head_office ?? true
          };

          setCompanyExists(true);
          setCompanyForm(formData);
          setCompanyFormInitial(formData);
          setCompanySuccessMessage(null);
          const resolvedWarning =
            company.tax_setup_warning ||
            (!company.tax_account_id ? TAX_ACCOUNT_WARNING_DEFAULT : null);
          setCompanyWarning(filterTaxIdWarning(resolvedWarning));
          
          // Check if user has transactions
          try {
            const transactionsResponse = await api.get(`/api/v1/platforms/transactions?company_id=${user.primary_company_id}&limit=1`);
            const count = transactionsResponse.data?.total || 0;
            setTransactionCount(count);
            setHasTransactions(count > 0);
            // Transaction count fetched; verbose onboarding log removed for production
          } catch (txError) {
            logger.warn('[OnboardingWizard] Failed to check transactions:', txError);
            setHasTransactions(false);
            setTransactionCount(0);
          }
        }
      } catch (error: any) {
        logger.error('[OnboardingWizard] Failed to load company data:', error);
        // If 404, company doesn't exist - we'll create it instead of updating
        if (error.message && error.message.includes('404')) {
          logger.warn('[OnboardingWizard] Company not found (404), will create new company');
          setCompanyExists(false);
          setCompanyForm(emptyCompanyForm);
          setCompanyFormInitial(emptyCompanyForm);
          setCompanySuccessMessage(null);
          setCompanyWarning(null);
        }
      }
    };
    
    loadCompanyData();
  }, [user]);

  // Resume onboarding after returning from the QuickBooks/Shopify OAuth redirect
  // (/get-started?quickbooks=connected): go straight back to the import step.
  const oauthResumeHandled = useRef(false);
  useEffect(() => {
    if (oauthResumeHandled.current || !user) return;
    const params = new URLSearchParams(window.location.search);
    const platform = params.get('quickbooks') === 'connected'
      ? 'quickbooks'
      : params.get('shopify') === 'connected' ? 'shopify' : null;
    if (!platform) return;
    oauthResumeHandled.current = true;
    setSelectedImportMethod(platform);
    setImportComplete(true);
    setActiveStep(3);
  }, [user]);

  // Handle step transitions based on authentication state and company/transaction status
  useEffect(() => {
    // If we're on the sign-in/register step and user becomes authenticated
    if (activeStep === 1 && user) {
      const hasPlaceholderCompany = isPlaceholderCompanyName(companyForm.name, user.full_name);
      if (user.primary_company_id && companyForm.name && !hasPlaceholderCompany) {
        // If company is valid and has transactions, skip to success step
        if (hasTransactions) {
          // Skipping to success step (removed verbose log)
          setActiveStep(4);
        } else {
          // Company is valid but no transactions, go to import step
          // Proceeding to import step (removed verbose log)
          setActiveStep(3);
        }
      } else {
        // No company or placeholder company name, go to company update step
        // User must create/update company (removed verbose log)
        setActiveStep(2);
      }
    } else if (activeStep === 1 && !user) {
    }
  }, [activeStep, user, registerError, companyForm.name, hasTransactions]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
      setUploadedFile(file);
      setUploadError(null);
    } else {
      setUploadError('Please select a valid CSV file');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    },
    multiple: false
  });

  const handleClose = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
    onClose();
  };

  const saveCompany = async (): Promise<SaveCompanyResult> => {
    setCompanyError(null);
    setCompanySuccessMessage(null);
    setCompanyWarning(null);

    if (!user || !isAuthenticated) {
      setCompanyError('You must be logged in to update your company.');
      logout();
      navigate('/login');
      return null;
    }

    const currentCompanyId = user.primary_company_id || createdCompanyId || null;
    const isNewCompany = !companyExists || !currentCompanyId;

    setCompanyLoading(true);

    try {
      if (
        !companyForm.name ||
        !companyForm.tax_id ||
        !companyForm.email ||
        !companyForm.phone ||
        !companyForm.website ||
        !companyForm.address_line1 ||
        !companyForm.city ||
        !companyForm.state ||
        !companyForm.zip_code ||
        !companyForm.country
      ) {
        setCompanyError('Please fill in all required fields');
        return null;
      }

      if (!/\S+@\S+\.\S+/.test(companyForm.email)) {
        setCompanyError('Please enter a valid email address');
        return null;
      }

      if (!companyForm.website.startsWith('http://') && !companyForm.website.startsWith('https://')) {
        setCompanyError('Website must start with http:// or https://');
        return null;
      }

      if (!/^https?:\/\/[a-zA-Z0-9][-a-zA-Z0-9.]*\.[a-zA-Z]{2,}(\/.*)?$/.test(companyForm.website)) {
        setCompanyError('Please enter a valid website URL');
        return null;
      }

      const phoneDigits = companyForm.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        setCompanyError('Phone number must have at least 10 digits');
        return null;
      }

      if (!/^\+?[1-9]\d{1,14}$/.test(companyForm.phone.startsWith('+') ? companyForm.phone : `+${phoneDigits}`)) {
        setCompanyError('Phone number should be in international format (e.g., +14155550199)');
        return null;
      }

      if (isPlaceholderCompanyName(companyForm.name, user.full_name)) {
        setCompanyError('Please update the auto-generated company name to reflect your actual business.');
        return null;
      }

      if (!companyForm.tax_code || companyForm.tax_code.trim().length === 0) {
        setCompanyError('Please select a product tax code for your company');
        return null;
      }

      if (!isValidEin(companyForm.tax_id, (companyForm as any).country)) {
        setCompanyError(EIN_ERROR);
        return null;
      }

      let response: CompanyApiEntity = null;

      if (isNewCompany) {
        logger.log('[OnboardingWizard] Creating new company since company_id reference is invalid or missing');
        response = await createCompany(companyForm);
        if (response) {
          const newCompanyId = extractCompanyId(response);
          if (newCompanyId) {
            setCreatedCompanyId(newCompanyId);
            setImportCompanyId(newCompanyId);
          }
          setCompanyExists(true);
        }
      } else if (currentCompanyId) {
        logger.log('[OnboardingWizard] Updating existing company');
        response = await companyService.updateCompany(currentCompanyId, companyForm);
        const updatedCompanyId = extractCompanyId(response);
        if (updatedCompanyId) {
          setImportCompanyId(updatedCompanyId);
        }
      }

      if (!response) {
        setCompanyError('Failed to save company. Please try again.');
        return null;
      }

      try {
        await refreshUser();
      } catch (userError) {
        logger.warn('[OnboardingWizard] Failed to refresh user context:', userError);
      }

      await loadImportCompanies();

      const companyEntity = isCompanyEntity(response) ? response : null;
      const resolvedWarning =
        companyEntity?.tax_setup_warning ||
        (companyEntity && !companyEntity.tax_account_id ? TAX_ACCOUNT_WARNING_DEFAULT : null);
      setCompanyWarning(filterTaxIdWarning(resolvedWarning));

      const normalizedForm: CompanyFormState = {
        name: companyEntity?.name || companyForm.name,
        tax_id: companyEntity?.tax_id || companyForm.tax_id,
        email: companyEntity?.email || companyForm.email,
        phone: companyEntity?.phone || companyForm.phone,
        website: companyEntity?.website || companyForm.website,
        address_line1: companyEntity?.address_line1 || companyForm.address_line1,
        city: companyEntity?.city || companyForm.city,
        state: companyEntity?.state || companyForm.state,
        zip_code: companyEntity?.zip_code || companyForm.zip_code,
        country: companyEntity?.country || companyForm.country || 'US',
        tax_code: companyEntity?.tax_code || companyForm.tax_code,
        is_head_office:
          typeof companyEntity?.is_head_office === 'boolean'
            ? companyEntity.is_head_office
            : companyForm.is_head_office,
      };

      setCompanyForm(normalizedForm);
      setCompanyFormInitial(normalizedForm);
      setCompanySuccessMessage(isNewCompany ? 'Company created successfully.' : 'Company details updated successfully.');

      return isNewCompany ? 'created' : 'updated';
    } catch (error: any) {
      logger.error('[OnboardingWizard] Company update error:', error);

      if (error instanceof AuthenticationError) {
        setCompanyError(error.message);
        logout();
        navigate('/login');
        return null;
      }

      setCompanyError(error.message || 'Failed to update company. Please try again.');
      return null;
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === steps.length - 1) {
      onClose();
      navigate('/dashboard');
      return;
    }

    if (companyLoading || importing) {
      return;
    }

    if (activeStep === 2) {
      if (!companyExists || hasCompanyChanges) {
        const result = await saveCompany();
        if (!result) {
          return;
        }
      }
      setActiveStep(3);
      return;
    }

    if (activeStep === 3) {
      if (importComplete || hasTransactions) {
        setActiveStep((prevStep) => prevStep + 1);
        return;
      }

      if (selectedImportMethod) {
        await handleImport();
        return;
      }

      setActiveStep((prevStep) => prevStep + 1);
      return;
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleAuthSuccess = () => {
    // After successful authentication, go to company creation step
    setActiveStep(2); // Go to company creation step
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleImportMethodSelect = (method: string) => {
    setUploadError(null);
    setImportComplete(false);
    setSelectedImportMethod(method);
  };

  const handleAddressChange = (address: string, addressData: any) => {
    setCompanyForm(prev => ({
      ...prev,
      address_line1: addressData.address_line1 || prev.address_line1,
      city: addressData.city || prev.city,
      state: addressData.state || prev.state,
      zip_code: addressData.zip_code || prev.zip_code,
      country: addressData.country || prev.country
    }));
  };

  const handleCompanySubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const result = await saveCompany();

    if (result === 'created') {
      setActiveStep(3);
    }
  };

  // ---- Tax code dialog handlers ----
  const handleTaxDialogClose = () => {
    setTaxDialogOpen(false);
    // If user skipped selection just go to next step
    setActiveStep(3);
  };

  const handleTaxCodeSelect = async (code: string) => {
    if (!createdCompanyId) {
      handleTaxDialogClose();
      return;
    }
    try {
      await companyService.updateCompany(createdCompanyId, { tax_code: code });
      // Refresh user/company data
      await refreshUser();
    } catch (err) {
    } finally {
      handleTaxDialogClose();
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoginLoading(true);
    try {
      // Custom login without navigation
      const formData = new URLSearchParams();
      formData.append('username', loginForm.email);
      formData.append('password', loginForm.password);
      const response = await fetch(`${API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        },
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 401 && errorData.detail && errorData.detail.includes('Incorrect email or password')) {
          setLoginError('Incorrect email or password');
        } else if (!window.navigator.onLine) {
          setLoginError('Network error. Please check your connection and try again.');
        } else {
          setLoginError(errorData.detail || 'Login failed');
        }
        setLoginLoading(false);
        return;
      }
      const data = await response.json();
      localStorage.setItem('token', data.access_token);
      // Fetch user data
      const userResponse = await fetch(`${API_URL}/api/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${data.access_token}`,
          'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
        }
      });
      if (userResponse.ok) {
        const userData = await userResponse.json();
        login({ email: loginForm.email, password: loginForm.password });
        setLoginLoading(false);
        handleAuthSuccess(); // Always advance to Import Transactions
      } else {
        setLoginError('Failed to fetch user data');
        setLoginLoading(false);
      }
    } catch (err: any) {
      setLoginError('Network error. Please check your connection and try again.');
      setLoginLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError(null);
    
    // Client-side validation
    const validation: { [key: string]: string } = {};
    if (!registerForm.email) validation.email = 'Email is required';
    if (!registerForm.password) validation.password = 'Password is required';
    if (registerForm.password !== registerForm.confirmPassword) {
      validation.confirmPassword = 'Passwords do not match';
    }
    if (!registerForm.full_name) validation.full_name = 'Full name is required';
    
    if (Object.keys(validation).length > 0) {
      setRegisterValidation(validation);
      return;
    }
    
    setRegisterLoading(true);
    
    try {
      const userData = await register({
        email: registerForm.email,
        password: registerForm.password,
        confirmPassword: registerForm.confirmPassword,
        full_name: registerForm.full_name,
      });
      
      if (userData) {
        // The useEffect will handle advancing to the next step when user is set
      }
    } catch (err: any) {
      // Use the error message from the error object which comes from AuthContext
      setRegisterError(err.message || 'An error occurred during registration. Please try again.');
      setRegisterLoading(false);
      
      // Scroll to the top to show the error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCSVUpload = async () => {
    if (!uploadedFile) {
      setUploadError('Please select a CSV file');
      return;
    }
    if (importDateRangeError) {
      setUploadError(importDateRangeError);
      return;
    }

    const companyIdForUpload = importCompanyId || createdCompanyId || user?.primary_company_id || 0;
    if (!companyIdForUpload) {
      setUploadError('Please select a company before importing.');
      return;
    }

    if (!user) {
      setUploadError('You must be logged in to import transactions.');
      return;
    }

    setImporting(true);
    setUploadProgress(0);
    setUploadError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('platform_name', 'CSV');
      formData.append('company_id', String(companyIdForUpload));
      const { startDate, endDate } = sanitizeImportDateRange(importDateRange);
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Make API call to upload CSV with explicit company_id to avoid backend defaulting
      const companyIdParam = companyIdForUpload ? `?company_id=${companyIdForUpload}` : '';
      const response = await fetch(`${API_URL}/api/v1/platforms/csv/upload${companyIdParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const result = await response.json();
      
      setImportCompanyId(companyIdForUpload);
      setImportComplete(true);
      setImporting(false);
      setHasTransactions(true);
      setTransactionCount(result.imported_count || 1);
      
      logger.log('[OnboardingWizard] Successfully imported transactions, advancing to success step');
      
      // Navigate to complete tax view - advance directly instead of navigating
      setTimeout(() => {
        setActiveStep(4); // Go to success step
      }, 1000);

    } catch (error: any) {
      logger.error('CSV upload error:', error);
      setUploadError(error.message || 'Upload failed. Please try again.');
      setImporting(false);
      setUploadProgress(0);
    }
  };

  const handleImport = async () => {
    setUploadError(null);

    if (!selectedImportMethod) {
      return;
    }

    if (selectedImportMethod === 'upload') {
      await handleCSVUpload();
      return;
    }

    if (!user) {
      setUploadError('You must be logged in to connect integrations.');
      setImporting(false);
      return;
    }

    const companyIdForIntegration = importCompanyId || createdCompanyId || user?.primary_company_id || 0;
    if (!companyIdForIntegration) {
      setUploadError('Please select a company before continuing.');
      setImporting(false);
      return;
    }

    if (selectedImportMethod === 'quickbooks') {
      if (importDateRangeError) {
        setUploadError(importDateRangeError);
        setImporting(false);
        return;
      }
      await sharedHandleQuickBooksImport({
        source: 'onboarding',
        user: {
          id: user.id,
          email: user.email,
          company_id: companyIdForIntegration
        },
        setUploadError,
        setImporting,
        navigate,
        companyId: companyIdForIntegration,
        dateRange: importDateRange
      });
      return;
    }

    if (selectedImportMethod === 'shopify') {
      if (!shopifyStore) {
        setUploadError('Please enter your Shopify store name.');
        setImporting(false);
        return;
      }
      if (importDateRangeError) {
        setUploadError(importDateRangeError);
        setImporting(false);
        return;
      }
      await sharedHandleShopifyImport({
        source: 'onboarding',
        user: {
          id: user.id,
          email: user.email,
          company_id: companyIdForIntegration
        },
        setUploadError,
        setImporting,
        navigate,
        companyId: companyIdForIntegration,
        shop: shopifyStore,
        dateRange: importDateRange
      });
      return;
    }
  };

  const renderStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              Welcome to GoTruTax! 🎉
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We're here to make your sales tax compliance effortless. In the next few steps, we'll help you:
            </Typography>
            <Box sx={{ mt: 3 }}>
              {[
                'Create your account and company profile',
                'Import your sales transactions',
                'Calculate taxes automatically',
                'Get comprehensive tax insights',
                'Stay compliant with ease'
              ].map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircle sx={{ color: 'success.main', mr: 2 }} />
                  <Typography variant="body1">{item}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {!!user ? 'Welcome Back!' : 'Join GoTruTax Today'}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {!!user ? 'You\'re all set to continue with your tax compliance journey.' : 'Start your tax compliance journey with a free account.'}
            </Typography>
            {!user && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Button
                    variant={!showLogin ? 'outlined' : 'contained'}
                    onClick={() => setShowLogin(false)}
                    sx={{ 
                      background: !showLogin ? 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' : undefined,
                      color: !showLogin ? 'white' : undefined,
                      '&:hover': !showLogin ? {
                        background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)'
                      } : undefined
                    }}
                  >
                    Sign Up - It's Free! 🎉
                  </Button>
                  <Button
                    variant={showLogin ? 'outlined' : 'contained'}
                    onClick={() => setShowLogin(true)}
                    sx={{ ml: 1 }}
                  >
                    Sign In
                  </Button>
                </Box>
                
                {/* Google Sign-In Section */}
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Quick sign-in with Google
                  </Typography>
                  <GoogleSignIn 
                    onSuccess={handleAuthSuccess}
                    onError={(error: string) => setRegisterError(error)}
                  />
                </Box>
                
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    or continue with email
                  </Typography>
                </Divider>
                
                {showLogin ? (
                  <Login showGoogle={false} />
                ) : (
                  <Register showGoogle={false} />
                )}
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {companyExists ? 'Update Your Company Details' : 'Create Your Company'}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please {companyExists ? 'update' : 'enter'} your company details for accurate tax compliance. Make sure to provide your actual company name and select a product tax code.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> Rename the auto-generated company, add a business email, phone, and website, and select a product tax code before you can calculate taxes.
              </Typography>
            </Alert>
            
            {companyError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {companyError}
              </Alert>
            )}

            {companySuccessMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {companySuccessMessage}
              </Alert>
            )}

            {companyWarning && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {companyWarning}
              </Alert>
            )}
            
            <Box component="div" sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={companyForm.name}
                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tax ID (EIN)"
                    value={companyForm.tax_id}
                    onChange={(e) => setCompanyForm({ ...companyForm, tax_id: e.target.value })}
                    required
                    variant="outlined"
                    placeholder="12-3456789"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Business Email"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                    required
                    type="email"
                    variant="outlined"
                    placeholder="owner@yourcompany.com"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Business Phone"
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    required
                    variant="outlined"
                    placeholder="+14155550199"
                    helperText="Use international format (e.g., +14155550199)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Business Website"
                    value={companyForm.website}
                    onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                    required
                    variant="outlined"
                    placeholder="https://www.yourcompany.com"
                  />
                </Grid>
                <Grid item xs={12}>
                  <GooglePlacesAutocompleteProxy
                    value={companyForm.address_line1}
                    onChange={handleAddressChange}
                    label="Company Address"
                    required
                    disabled={companyLoading}
                    placeholder="Enter company address..."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={companyForm.city}
                    onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                    required
                    variant="outlined"
                    disabled={companyLoading}
                    InputProps={{
                      readOnly: !!companyForm.city
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State"
                    value={companyForm.state}
                    onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })}
                    required
                    variant="outlined"
                    disabled={companyLoading}
                    InputProps={{
                      readOnly: !!companyForm.state
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={companyForm.zip_code}
                    onChange={(e) => setCompanyForm({ ...companyForm, zip_code: e.target.value })}
                    required
                    variant="outlined"
                    disabled={companyLoading}
                    InputProps={{
                      readOnly: !!companyForm.zip_code
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={companyForm.country}
                    onChange={(e) => setCompanyForm({ ...companyForm, country: e.target.value })}
                    variant="outlined"
                    required
                    select
                    disabled={companyLoading}
                    InputProps={{
                      readOnly: !!companyForm.country && companyForm.country !== 'US'
                    }}
                  >
                    <MenuItem value="US">United States</MenuItem>
                    <MenuItem value="CA">Canada</MenuItem>
                    <MenuItem value="MX">Mexico</MenuItem>
                    <MenuItem value="GB">United Kingdom</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <TextField
                      sx={{ flexGrow: 1, mr: 1 }}
                      InputProps={{
                        readOnly: true
                      }}
                      fullWidth
                      label="Product Tax Code"
                      value={companyForm.tax_code || ''}
                      variant="outlined"
                      placeholder="Select a tax code for your products"
                      helperText="The tax code determines how your products and services are taxed"
                    />
                    <Button 
                      variant="outlined"
                      color="primary" 
                      onClick={() => setTaxDialogOpen(true)}
                      disabled={companyLoading}
                    >
                      Select
                    </Button>
                    {companyForm.tax_code && (
                      <Button 
                        variant="text"
                        color="secondary" 
                        onClick={() => setCompanyForm({ ...companyForm, tax_code: '' })}
                        disabled={companyLoading}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={companyForm.is_head_office}
                        onChange={(e) => setCompanyForm({ ...companyForm, is_head_office: e.target.checked })}
                        name="is_head_office"
                        disabled={companyLoading}
                      />
                    }
                    label="Head Office Address"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    This address will be used for tax calculation and nexus determination for sales tax registration
                  </Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(1)}
                  disabled={companyLoading}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  disabled={companyLoading}
                  sx={{ minWidth: 120 }}
                  type="button"
                  onClick={() => handleCompanySubmit()}
                >
                  {companyLoading ? <CircularProgress size={20} /> : (companyExists ? 'Update Company' : 'Create Company')}
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Choose Your Import Method
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select the best way to import your sales transactions:
            </Typography>
              {registerError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {registerError}
                </Alert>
              )}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Import for Company
                </Typography>
                <CompanySelector
                  selectedCompanyId={importCompanyId}
                  onCompanyChange={(cid) => setImportCompanyId(cid)}
                  companies={importCompanies}
                  loading={importCompaniesLoading}
                  error={importCompanyError}
                  onRefresh={loadImportCompanies}
                  includeAllOption={false}
                  disabled={importCompaniesLoading || importCompanies.length <= 1}
                />
              </Box>
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {importOptions.map((option) => (
                <Grid item xs={12} md={4} key={option.action}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedImportMethod === option.action ? 2 : 1,
                      borderColor: selectedImportMethod === option.action ? 'primary.main' : 'divider',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => handleImportMethodSelect(option.action)}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Box sx={{ color: 'primary.main', mb: 2 }}>
                        {option.icon}
                      </Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {option.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {option.description}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {option.features.map((feature, index) => (
                          <Chip
                            key={index}
                            label={feature}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {selectedImportMethod && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Selected: <strong>{importOptions.find(opt => opt.action === selectedImportMethod)?.title}</strong>
                </Typography>
                  <Button variant="contained" onClick={handleImport} disabled={importing || !importCompanyId || (selectedImportMethod === 'upload' && !uploadedFile)}>
                  {selectedImportMethod === 'upload' ? 'Upload CSV' : 'Start Import'}
                </Button>
              </Box>
            )}

            {selectedImportMethod === 'upload' && (
              <Box sx={{ mt: 3 }}>
                {/* File Upload Area */}
                <Box
                  {...getRootProps()}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragActive ? 'primary.light' : 'background.paper',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'primary.light',
                    },
                  }}
                >
                  <input {...getInputProps()} />
                  <FileUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  {uploadedFile ? (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        File Selected: {uploadedFile.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Size: {(uploadedFile.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {isDragActive ? 'Drop the CSV file here' : 'Drag & drop a CSV file here'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        or click to browse files
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Sample CSV Download */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleImport}
                    disabled={!uploadedFile || importing}
                  >
                    Upload CSV
                  </Button>
                  <Button
                    startIcon={<Download />}
                    component="a"
                    href={SAMPLE_CSV_URL}
                    download
                    sx={{ mt: 1 }}
                  >
                    Download Sample CSV
                  </Button>
                </Box>

                {/* Upload Progress */}
                {importing && (
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Uploading... {uploadProgress}%
                      </Typography>
                    </Box>
                    <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1 }}>
                      <Box
                        sx={{
                          width: `${uploadProgress}%`,
                          height: 8,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Error Display */}
                {uploadError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {uploadError}
                  </Alert>
                )}
                <Box sx={{ mt: 3 }}>
                  <ImportDateRangeControls
                    range={importDateRange}
                    onChange={setImportDateRange}
                    helperText="Only transactions within this range will be processed after upload."
                    disabled={importing}
                    errorText={importDateRangeError}
                  />
                </Box>
              </Box>
            )}

            {selectedImportMethod === 'quickbooks' && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" paragraph>
                  We'll connect to your QuickBooks account and import all your transaction data automatically.
                </Typography>
                <Box sx={{ maxWidth: 520, mx: 'auto', textAlign: 'left', mb: 2 }}>
                  <ImportDateRangeControls
                    range={importDateRange}
                    onChange={setImportDateRange}
                    helperText="Leave the fields blank to import the last 365 days."
                    disabled={importing}
                    errorText={importDateRangeError}
                  />
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Using your existing authentication to connect securely to QuickBooks.
                  </Typography>
                </Alert>
              </Box>
            )}

            {selectedImportMethod === 'shopify' && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <TextField
                  label="Shopify Store Name"
                  value={shopifyStore}
                  onChange={e => setShopifyStore(e.target.value)}
                  required
                  fullWidth
                  margin="normal"
                  placeholder="yourstore (for yourstore.myshopify.com)"
                />
                <Typography variant="body1" color="text.secondary" paragraph>
                  We'll connect to your Shopify store and import all your order data automatically.
                </Typography>
                <Box sx={{ maxWidth: 520, mx: 'auto', textAlign: 'left', mb: 2 }}>
                  <ImportDateRangeControls
                    range={importDateRange}
                    onChange={setImportDateRange}
                    helperText="Leave the fields blank to import the last 365 days."
                    disabled={importing}
                    errorText={importDateRangeError}
                  />
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Using your existing authentication to connect securely to Shopify.
                  </Typography>
                </Alert>
              </Box>
            )}

            {importing ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body1">
                  {selectedImportMethod === 'upload' && 'Uploading your transactions...'}
                  {selectedImportMethod === 'quickbooks' && 'Connecting to QuickBooks and importing data...'}
                  {selectedImportMethod === 'shopify' && 'Connecting to Shopify and importing data...'}
                </Typography>
              </Box>
            ) : importComplete ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body1">
                  ✅ Successfully imported your transactions!
                </Typography>
              </Alert>
            ) : null}
          </Box>
        );

      case 4:
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            {hasTransactions ? (
              <>
                <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Tax Insights Ready! 📊
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Your transactions have been imported and tax calculations are complete. 
                  You can now view comprehensive tax insights and manage your compliance.
                </Typography>
                <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>
                  <Typography variant="body2">
                    ✅ {transactionCount} transaction{transactionCount !== 1 ? 's' : ''} imported successfully!
                  </Typography>
                </Alert>
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/complete-tax-view')}
                    endIcon={<ArrowForward />}
                    sx={{ mr: 2 }}
                  >
                    View Tax Insights
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleNext}
                  >
                    Go to Dashboard
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Warning sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  No Transactions Imported
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  You need to import at least one transaction before you can view tax insights or calculate taxes.
                </Typography>
                <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                  <Typography variant="body2">
                    <strong>Important:</strong> Tax calculations cannot be performed until you import transactions. Please go back to the import step.
                  </Typography>
                </Alert>
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setActiveStep(3)}
                    startIcon={<ArrowBack />}
                  >
                    Go Back to Import
                  </Button>
                </Box>
              </>
            )}
          </Box>
        );

      case 5:
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              You're All Set! 🎉
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your tax compliance journey begins now. You can access all features from your dashboard.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleNext}
                endIcon={<ArrowForward />}
                sx={{ mr: 2 }}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/complete-tax-view')}
              >
                View Tax Insights
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  // If not open, don't render anything
  if (!open) {
    return null;
  }

  return (
    <>
      <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '70vh',
          // phones: use nearly the full width instead of MUI's 32px side margins
          m: { xs: 1, sm: 4 },
          width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {steps[activeStep].label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Need help?">
              <IconButton size="small" aria-label="Need help?" component="a" href="/contact" target="_blank" rel="noopener noreferrer">
                <Help />
              </IconButton>
            </Tooltip>
            <IconButton aria-label="close" onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex' }}>
          {/* The 300px step rail clipped the form (incl. Google sign-in) at 390px; hide it below md - the title names the step. */}
          <Box sx={{ width: 300, flexShrink: 0, p: 3, borderRight: 1, borderColor: 'divider', display: { xs: 'none', md: 'block' } }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box sx={{ color: index <= activeStep ? 'primary.main' : 'grey.400' }}>
                        {step.icon}
                      </Box>
                    )}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {step.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {step.description}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {renderStepContent(activeStep)}
            </motion.div>
          </Box>
        </Box>
      </DialogContent>

        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          {activeStep > 0 && (
          <Button type="button" onClick={handleBack} color="inherit">
              Back
            </Button>
          )}
        {/* Only show Next button if not on sign-in/register step */}
        {activeStep !== 1 && (
            <Button
              type="button"
              variant="contained"
              onClick={handleNext}
              color="primary"
              disabled={
              importing || loginLoading || registerLoading || companyLoading ||
              (activeStep === 3 &&
                !!selectedImportMethod &&
                !importComplete &&
                !hasTransactions &&
                (!importCompanyId || (selectedImportMethod === 'upload' && !uploadedFile)))
            }
            >
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
            </Button>
          )}
      </DialogActions>
    </Dialog>
      <ProductTaxCodeDialog
        open={taxDialogOpen}
        onClose={handleTaxDialogClose}
        onSelect={handleTaxCodeSelect}
      />
    </>
  );
};
